const COPILOT_SCRIPT_URL_PROD =
  'https://cdn.ai.chargebee.com/copilot-ui/assets/copilot-embed.js';
const COPILOT_SCRIPT_URL_DEV =
  'https://cdn.ai.devcb.in/copilot-ui/assets/copilot-embed.js';

const COPILOT_SCRIPT_URL =
  process.env.NODE_ENV === 'production'
    ? COPILOT_SCRIPT_URL_PROD
    : COPILOT_SCRIPT_URL_DEV;

const LOAD_TIMEOUT_MS = 10_000;

let loadPromise: Promise<void> | null = null;

/**
 * Loads the Chargebee Copilot embed script. Resolves when the script is loaded
 * and `window.ChargebeeCopilot` is available. Rejects on error or timeout.
 * Safe to call multiple times — deduplicates via `loadPromise` and retries on failure.
 */
export const loadCopilotScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Copilot script can only load in browser'));
  }

  if (window.ChargebeeCopilot) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${COPILOT_SCRIPT_URL}"]`
    );

    let cleanupListeners: (() => void) | null = null;
    let scriptRef: HTMLScriptElement | null = existingScript;

    const removeScript = () => {
      scriptRef?.remove();
      scriptRef = null;
    };

    const settle = () => {
      clearTimeout(timer);
      cleanupListeners?.();
    };

    const timer = setTimeout(() => {
      settle();
      removeScript();
      loadPromise = null;
      reject(new Error('Copilot script load timed out'));
    }, LOAD_TIMEOUT_MS);

    const onReady = () => {
      settle();
      if (window.ChargebeeCopilot) {
        resolve();
      } else {
        removeScript();
        loadPromise = null;
        reject(
          new Error('Copilot script loaded but ChargebeeCopilot not found')
        );
      }
    };

    const onFail = () => {
      settle();
      removeScript();
      loadPromise = null;
      reject(new Error('Failed to load Copilot script'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', onReady);
      existingScript.addEventListener('error', onFail);
      cleanupListeners = () => {
        existingScript.removeEventListener('load', onReady);
        existingScript.removeEventListener('error', onFail);
      };
      return;
    }

    const script = document.createElement('script');
    script.src = COPILOT_SCRIPT_URL;
    script.async = true;
    script.onload = onReady;
    script.onerror = onFail;
    document.head.appendChild(script);
    scriptRef = script;
  });

  return loadPromise;
};
